import { useDispatch, useSelector } from "react-redux";

// jazasoft
import { showNotification } from "jazasoft";

// helpers
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import { getDistinctValues, isEmpty, isFalse, isTrue, listFromCsv } from "../../../utils/helpers";
import { format } from "../../../utils/dateUtils";
import { Entity, DataType, OState, Role } from "../../../utils/types";
import { FlowInfoFields } from "../../../utils/entityFields";
import SettingKeys from "../../../utils/SettingKeys";
import getTableValidation from "../../../utils/getTableValidation";
import useSetting from "../../../hooks/useSetting";
import compressImage from "../../../utils/compressImage";
import useDeepMemo from "../../../hooks/useDeepMemo";
import { useDeepEffect, useSafeState, useTime, useUnit } from "../../../hooks";
import { OrderFlowType } from "../../../constants";

const stateValueMap = Object.values(OState).reduce((acc, e) => ({ ...acc, [e.state]: e.stateValue }), {});
const childKeys = [
  "style",
  "styleNo",
  "fit",
  "inseam",
  "destination",
  "delMode",
  "exFactoryDate",
  "extra",
  "shortShipPercent",
  "flowRef",
  "delFlowRef",
  "customerFlowRef",
  "customField1",
  "customField2",
  "customField3"
];

export const removeChildlessParents = rows => {
  const childlessParents = rows.filter(e => !isEmpty(e.id) && !isEmpty(e.isSetParent) && !rows.some(row => row.parentKey === e.key));
  return isEmpty(childlessParents) ? rows : removeChildlessParents(rows.filter(e => !childlessParents.includes(e)));
};

const isFPOCreated = (flowInfo, unitIds, order) => {
  const children = order?.flowInfoList?.filter(e => e.orderParentId === flowInfo?.id);
  return unitIds?.length > 1 && (!isEmpty(flowInfo?.fpo) || !isEmpty(children));
};

const isFabricAssociated = flowInfo => {
  const stateList = flowInfo?.stateList || [];
  return stateList.some(e => stateValueMap[e.state] > OState.NEW.stateValue);
};

// const isGroupingStarted = flowInfo => {
//   const stateList = flowInfo?.stateList || [];
//   return stateList.some(e => stateValueMap[e.state] >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue);
// };

const isCutOrderCreated = (flowInfo, order) => {
  const mFlowInfo = order?.flowInfoList?.find(e => e.id === flowInfo?.id);
  const cutOrders = order?.orderFlowList
    ?.filter(e => e.type === OrderFlowType.CUTPLAN)
    .filter(e => e.product?.id === mFlowInfo?.product?.id && e.styleList?.includes(mFlowInfo?.style) && e.colorList?.includes(mFlowInfo?.color));
  return cutOrders?.length > 0;
};

const isCutPlanStarted = flowInfo => {
  const stateList = flowInfo?.stateList || [];
  return stateList.some(e => stateValueMap[e.state] > OState.FINAL_GROUPING_COMPLETED.stateValue);
};

const isClosed = flowInfo => {
  const stateList = flowInfo?.stateList || [];
  return !isEmpty(stateList) && stateList.some(e => stateValueMap[e.state] === OState.CLOSED.stateValue);
};

const getColumnProps = validation => {
  let columnProps = {};
  switch (validation?.dataType) {
    case DataType.TEXT:
      columnProps = {
        inputType: "text-input",
        validate: getTableValidation(validation)
      };
      break;
    case DataType.NUMBER:
      columnProps = {
        inputType: "text-input",
        options: { type: "number" },
        validate: getTableValidation(validation)
      };
      break;
    case DataType.BOOLEAN:
      columnProps = {
        inputType: "boolean-input",
        validate: getTableValidation(validation)
      };
      break;
    case DataType.DATE:
      columnProps = {
        inputType: "text-input",
        options: { type: "date" },
        validate: getTableValidation(validation)
      };
      break;
    case DataType.SELECT:
      columnProps = {
        inputType: "select-input",
        choices: validation.choices
          .split(",")
          .map(e => e.trim())
          .map(e => ({ id: e, name: e })),
        validate: getTableValidation(validation)
      };
      break;
    default:
      columnProps = {};
  }
  return columnProps;
};

const useCreateEdit = props => {
  const { id, i18nKey, history, roles = [], basePath } = props;
  const resource = "orders";

  // hooks
  const dispatch = useDispatch();
  const formValues = useSelector(state => state?.form && state.form["record-form"]?.values);

  const [showRatio, setShowRatio] = useSafeState(false);
  const [mutate, { loading: saving }] = useMutation();
  const { utc_offset } = useTime();
  const { allUnitList } = useUnit();

  const { data: order = {} } = useQuery(id && { url: `orders/${id}` });
  const { data: productList = [] } = useQuery({
    url: "products",
    params: { size: 1000, sort: "name,asc" }
  });

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const orderValidationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, resource);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);
  const flowInfoValidationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, Entity.FLOW_INFO);

  const orderCustomMultiFields = Object.keys(orderValidationRule || {}).filter(
    key => key.startsWith("customField") && orderValidationRule[key]?.dataType === DataType.SELECT && isTrue(orderValidationRule[key]?.multiple)
  );
  const flowCustomMultiFields = Object.keys(flowInfoValidationRule || {}).filter(
    key => key.startsWith("customField") && flowInfoValidationRule[key]?.dataType === DataType.SELECT && isTrue(flowInfoValidationRule[key]?.multiple)
  );
  let flowInfoDefaultValue = !isEmpty(flowInfoValidationRule)
    ? Object.keys(flowInfoValidationRule)
        .filter(key => !isEmpty(flowInfoValidationRule[key]?.defaultValue))
        .reduce((acc, key) => ({ ...acc, [key]: flowInfoValidationRule[key]?.defaultValue }), {})
    : {};

  const onSubmit = async (allRows, _, onSubmitComplete) => {
    const rows = removeChildlessParents(allRows);

    const errors = rows
      .map(row => {
        const setParent = rows.find(e => e.key === row.parentKey && (isEmpty(e.id) || e.isSetParent));
        if (isEmpty(setParent)) return null;
        if (isEmpty(row.ratio) || row.ratio <= 0) {
          return "Positive ratio value for child product is required";
        }
        return null;
      })
      .filter(e => !isEmpty(e));
    if (!isEmpty(errors)) {
      dispatch(showNotification(errors[0], { type: "danger" }));
      onSubmitComplete(allRows);
      return;
    }

    const data = {
      ...formValues,
      ...orderCustomMultiFields.reduce((acc, field) => ({ ...acc, [field]: formValues[field]?.join(",") }), {})
    };
    const flowInfoList = rows.map((e, idx) => {
      const children = rows.filter(row => row.parentKey === e.key);
      return {
        ...flowInfoDefaultValue,
        ...e,
        ...flowCustomMultiFields.reduce((acc, field) => ({ ...acc, [field]: e[field]?.join(",") }), {}),
        deliveryDate: e.exFactoryDate,
        serialNo: idx + 1,
        styleImage: e.styleImage?.name,
        // isSetParent: e.hasChild ? rows.filter(row => row.parentKey === e.key).some(row => !isEmpty(row.ratio)) : null
        isSetParent: !isEmpty(children) ? children.some(row => !isEmpty(row.ratio)) : null
        // isSetParent: e.hasChild ? isTrue(productList.find(p => p.id === e.productId)?.isSet) : null
      };
    });

    const styleImageFiles = rows.filter(e => e.styleImage instanceof File).map(e => e.styleImage);
    const compressedFiles = await Promise.all(
      styleImageFiles.map(file => {
        const size = Math.round((file?.size || 0) / (1024 * 1024));
        const quality = size > 5 ? 0.2 : size > 3 ? 0.3 : 0.4;
        return compressImage(file, quality);
      })
    );

    const body = { ...data, flowInfoList };

    const formData = new FormData();
    formData.append("body", JSON.stringify(body));
    compressedFiles.forEach(styleImageFile => formData.append("styleImages", styleImageFile));

    const options = {
      url: id ? `${resource}/${id}` : `${resource}/multipart`,
      method: id ? "patch" : "post",
      params: { action: "default" },
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(
          showNotification(id ? "js.notification.updated" : "js.notification.created", {
            resource: i18nKey,
            messageArgs: { smart_count: 1 }
          })
        );
        history.push(`${basePath}/${data?.id}/view`);
        onSubmitComplete && onSubmitComplete();
      },
      onFailure: () => onSubmitComplete && onSubmitComplete()
    });
  };

  // const onValidate = values => {
  //   const rows = values?.rows || [];

  //   const errors = rows.map(row => {
  //     const parent = rows.find(e => e.key === row.parentKey && (isEmpty(e.id) || e.isSetParent));
  //     if (isEmpty(parent)) return {};
  //     if (isEmpty(row.ratio)) {
  //       return { ratio: "Value Required" };
  //     } else if (row.ratio <= 0) {
  //       return { ratio: "Value must be positive" };
  //     }
  //     return {};
  //   });
  //   return { rows: errors };
  // };

  const onChange = (name, value, row, form) => {
    const dataKey = name.split(".")[1];
    const rows = form.getState()?.values?.rows || [];
    const rowIdx = Number(name.split(".")[0].replace("rows[", "").replace("]", ""));

    if (dataKey === "productId") {
      if (!isEmpty(row.parentKey)) {
        const parentRow = rows.find(e => e.key === row.parentKey);
        const parentProduct = productList.find(p => p.id === parentRow?.productId);
        const ratio = parentProduct?.productList?.find(p => p.product?.id === value)?.ratio;
        const orderQty = (parentRow?.orderQty || 0) * (ratio || 1);
        form.change(`${name.split(".")[0]}.ratio`, ratio);
        form.change(`${name.split(".")[0]}.orderQty`, orderQty);
      }
    }
    if (dataKey === "ratio") {
      if (!isEmpty(row.parentKey)) {
        const parentRow = rows.find(e => e.key === row.parentKey);
        const orderQty = (parentRow?.orderQty || 0) * (value || 1);
        form.change(`${name.split(".")[0]}.orderQty`, orderQty);
      }
    }

    // Update Child value on change of parent
    if (childKeys.includes(dataKey)) {
      const children = rows.filter(e => e.parentKey === row.key);
      children.forEach(child => {
        const childIdx = rows.findIndex(e => e.key === child.key);
        if (childIdx !== -1) {
          form.change(`rows[${childIdx}].${dataKey}`, value);
        }
      });
    }
    // Update child order qty on change of parent order qty
    if (dataKey === "orderQty") {
      const children = rows.filter(e => e.parentKey === row.key);
      children.forEach(child => {
        const childIdx = rows.findIndex(e => e.key === child.key);
        if (childIdx !== -1) {
          const ratio = child.ratio || 0;
          const orderQty = (value || 0) * ratio;
          form.change(`rows[${childIdx}].orderQty`, orderQty);
        }
      });
    }
    // Update child color on change of parent color
    if (dataKey === "color") {
      const children = rows.filter(e => e.parentKey === row.key);
      const childColors = getDistinctValues(children.map(c => c.color));
      children.forEach(child => {
        const childIdx = rows.findIndex(e => e.key === child.key);
        if (childIdx !== -1) {
          const childColor = rows[childIdx]?.color;
          const rowColor = row.color || "";
          if (childColors?.length <= 1 && rowColor === childColor) {
            form.change(`rows[${childIdx}].color`, value);
          }
        }
      });
    }

    const rowList = form.getState()?.values?.rows || [];
    const finalRows = [...rowList];
    finalRows.splice(rowIdx, 1, { ...row, [dataKey]: value });
    const productIds = getDistinctValues(finalRows.map(e => e.productId));
    const showRatio = productList.filter(p => productIds.includes(p.id)).some(p => isTrue(p.isSet));

    setShowRatio(showRatio);
  };

  useDeepEffect(() => {
    if (isEmpty(order?.flowInfoList)) return;
    const productIds = getDistinctValues(order.flowInfoList.map(fi => fi.productId || fi.product?.id));
    const showRatio = productList.filter(p => productIds.includes(p.id)).some(p => isTrue(p.isSet));
    setShowRatio(showRatio);
  }, [order, productList]);

  const isAdmin = roles?.includes(Role.ADMIN_USER) || roles?.includes(Role.SUPER_USER) || roles?.includes(Role.MASTER);

  const columns = useDeepMemo(() => {
    if (isEmpty(flowInfoFields)) return [];

    const choicesProduct = ({ row, rows }) => {
      let products = productList;
      if (!isEmpty(row.parentKey)) {
        const parentRow = rows.find(e => e.key === row.parentKey);
        //* Check whether parent is super parent *//
        const isSuperParent = rows.filter(e => e.parentKey === parentRow.key).some(e => e.hasChild);
        const parentProduct = productList.find(p => p.id === parentRow?.productId);
        if (isTrue(parentProduct?.isSet) && !isEmpty(parentProduct?.productList)) {
          if (isSuperParent) {
            products = [parentProduct];
          } else {
            const productIds = getDistinctValues([...parentProduct.productList.map(p => p.product?.id), row.productId]).filter(id => !isEmpty(id));
            products = productList.filter(p => productIds.includes(p.id));
          }
        }
      }
      if (isTrue(row?.hasDestination)) {
        products = products.filter(p => !isTrue(p.isSet));
      }
      return products;
    };

    const disabledRatio = (row, dataKey, rows) => {
      let parentProduct;
      if (!isEmpty(row.parentKey)) {
        const parentRow = rows.find(e => e.key === row.parentKey);
        parentProduct = productList.find(p => p.id === parentRow?.productId);
      }
      return isFabricAssociated(row) || isEmpty(row.parentKey) || !isTrue(parentProduct?.isSet);
    };

    const disableChildProduct = (row, rows) => {
      if (!isEmpty(row.parentKey)) {
        const parentRow = rows.find(e => e.key === row.parentKey);
        //* Check whether parent is super parent *//
        const isSuperParent = rows.filter(e => e.parentKey === parentRow.key).some(e => e.hasChild);
        const parentProduct = productList.find(p => p.id === parentRow?.productId);
        return isTrue(parentProduct?.isSet) && !isSuperParent;
      }
      return false;
    };

    const disableChild = (row, rows) => {
      if (isEmpty(row?.parentKey)) return false;
      const parentRow = rows.find(e => e.key === row.parentKey);
      //* Check whether parent is super parent *//
      const isSuperParent = rows.filter(e => e.parentKey === parentRow.key).some(e => e.hasChild);
      const parentProduct = productList.find(p => p.id === parentRow?.productId);
      if (isSuperParent) return true;
      return isFalse(parentProduct?.isSet);
    };

    let columns = [
      {
        dataKey: FlowInfoFields.PRODUCT_ID,
        title: "Product",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.PRODUCT_ID]),
        inputType: "select-input",
        choices: choicesProduct,
        disabled: row => (isAdmin ? isCutPlanStarted(row) : isFabricAssociated(row))
      },
      {
        dataKey: FlowInfoFields.RATIO,
        title: "Ratio",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.RATIO]),
        disabled: (row, dataKey, rows) => disabledRatio(row, dataKey, rows) || isFabricAssociated(row) || isFPOCreated(row, allUnitList, order)
      },
      {
        dataKey: FlowInfoFields.STYLE_NO,
        title: "Style No",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.STYLE_NO]),
        disabled: (row, dataKey, rows) =>
          disableChildProduct(row, rows) || disableChild(row, rows) || (isAdmin ? isCutPlanStarted(row) : isFabricAssociated(row))
      },
      {
        dataKey: FlowInfoFields.STYLE,
        title: "Style",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.STYLE]),
        disabled: (row, dataKey, rows) =>
          disableChildProduct(row, rows) || disableChild(row, rows) || (isAdmin ? isCutPlanStarted(row) : isFabricAssociated(row))
      },
      {
        dataKey: FlowInfoFields.COLOR,
        title: "Style Color",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.COLOR]),
        disabled: (row, dataType, rows) => disableChild(row, rows) || (isAdmin ? isClosed(row) : isFabricAssociated(row))
      },
      {
        dataKey: FlowInfoFields.FIT,
        title: "Fit",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.FIT]),
        disabled: (row, dataKey, rows) =>
          disableChildProduct(row, rows) || disableChild(row, rows) || (isAdmin ? isCutPlanStarted(row) : isFabricAssociated(row))
      },
      {
        dataKey: FlowInfoFields.INSEAM,
        title: "Inseam",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.INSEAM]),
        disabled: (row, dataKey, rows) => isCutOrderCreated(row, order) || disableChildProduct(row, rows)
      },
      {
        dataKey: FlowInfoFields.DESTINATION,
        title: "Destination",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.DESTINATION]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.DEL_MODE,
        title: "Delivery Mode",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.DEL_MODE]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.GENDER,
        title: "Gender",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.GENDER]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.SLEEVE_TYPE,
        title: "Sleeve Type",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.SLEEVE_TYPE]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.EX_FACTORY_DATE,
        title: "Ex-Factory Date",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.EX_FACTORY_DATE]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.ORDER_QTY,
        title: "Order Qty",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.ORDER_QTY]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || isCutPlanStarted(row)
      },
      {
        dataKey: FlowInfoFields.EXTRA,
        title: "Extra",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.EXTRA]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || isCutPlanStarted(row)
      },
      {
        dataKey: FlowInfoFields.SHORT_SHIP,
        title: "Short Ship %",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.SHORT_SHIP]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || isCutPlanStarted(row)
      },
      {
        dataKey: FlowInfoFields.FLOW_REF,
        title: "Flow Ref.",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.FLOW_REF]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.DEL_FLOW_REF,
        title: "Del. Flow Ref.",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.DEL_FLOW_REF]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.CUSTOMER_FLOW_REF,
        title: "Customer Flow Ref.",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.CUSTOMER_FLOW_REF]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.CUSTOM_FIELD_1,
        title: "CustomField 1",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.CUSTOM_FIELD_1]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.CUSTOM_FIELD_2,
        title: "CustomField 2",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.CUSTOM_FIELD_2]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.CUSTOM_FIELD_3,
        title: "CustomField 3",
        ...getColumnProps(flowInfoValidationRule[FlowInfoFields.CUSTOM_FIELD_3]),
        disabled: (row, dataKey, rows) => disableChildProduct(row, rows) || (isAdmin ? isClosed(row) : isCutPlanStarted(row))
      },
      {
        dataKey: FlowInfoFields.STYLE_IMAGE,
        title: "Style Image",
        inputType: "file-input",
        options: { accept: { "image/png": [".png"], "image/jpeg": [".jpeg", ".jpg"], "image/gif": [".gif"] } },
        disabled: isClosed
      }
    ];

    if (!showRatio) {
      columns = columns.filter(c => c.dataKey !== FlowInfoFields.RATIO);
    }
    columns = columns.filter(c => c.dataKey === "ratio" || flowInfoFields.includes(c.dataKey));
    return columns;
  }, [flowInfoFields, flowInfoValidationRule, productList, showRatio, order]);

  const rows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) {
      return [{ ...flowInfoDefaultValue, key: new Date().getTime() }];
    }

    let rowList = order.flowInfoList
      .filter(fi => isEmpty(fi.orderParentId))
      .map(fi => ({ ...fi, key: fi.id, parentKey: fi.parentId }))
      .sort((a, b) => a.serialNo - b.serialNo)
      .map(fi => {
        return {
          ...fi,
          ...flowCustomMultiFields.reduce((acc, field) => ({ ...acc, [field]: listFromCsv(fi[field]) }), {}),
          productId: fi.product?.id,
          exFactoryDate: !isEmpty(fi.exFactoryDate) && utc_offset ? format(fi.exFactoryDate, utc_offset, "yyyy-MM-dd") : null
        };
      });
    return rowList;
  }, [order, flowInfoDefaultValue, utc_offset]);

  const showChildAddAction = (row, rows) => {
    const product = productList?.find(p => p.id === row?.productId);
    return isTrue(product?.isSet);
  };

  return {
    order,
    productList,
    orderFields,
    orderValidationRule,
    flowInfoValidationRule,
    saving,
    columns,
    rows,
    allUnitList,
    orderCustomMultiFields,
    showChildAddAction,
    onChange,
    // onValidate,
    onSubmit
  };
};

export default useCreateEdit;
